import { Inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from "@angular/common/http";
import { environment } from "../../../../environments/environment";
import { Statistic, Trip } from "../models/trip-overview"
import { Observable } from "rxjs";
import { SqiConfiguration } from "../../project/models/project-configuration";
import { TranslateService } from "@ngx-translate/core";
import { DatePipe } from "@angular/common";
import { TripDurationPipe } from "../pipes/trip-duration.pipe";
import { DataImportExportService } from "../../../shared/services/data-import-export.service";
import { ExportFilenameService } from '../../../shared/services/export-filename.service';
import { ReferenceArea } from '../../reference-areas/models/reference-area';

@Injectable({
  providedIn: 'root'
})
export class TripOverviewService {

  invalidSqiValue: number = -999;

  constructor(
      private translate: TranslateService,
      private datePipe: DatePipe,
      private dataImportExportService: DataImportExportService,
      private tripDurationPipe: TripDurationPipe) {}

  prepareTripData = (trip: Trip, projectSqiList: SqiConfiguration[], referenceAreas?: ReferenceArea[] | null) : Trip => {

    trip.statistics?.forEach(statistic => {
      // get specific metadata for the SQI id.
      let metaData = projectSqiList?.filter(projectSqi => projectSqi.id == statistic.sqiId) || [];

      if (metaData.length > 0) {
        statistic = this.extendWithSQIMetaData(statistic, metaData[0]);
      } else {
        console.log("Trip overview, trip search completed before project config loaded.");
      }
    });
    return trip;
  }

  extendWithSQIMetaData(statistic: Statistic, metaData: SqiConfiguration): Statistic {
    if (statistic) {
      statistic.cssClass = "sqi-" + this.createSQICSSClass(statistic);
      statistic.unit = metaData.unit;
      statistic.fullDescription = metaData.description;
      statistic.shortDescription = metaData.shortDescription;
      statistic.mdDocumentation = metaData.mdDocumentation;
    }

    return statistic;
  }

  createSQICSSClass(statistic: Statistic): string {
    let result = "";
    if (statistic && statistic.value != undefined) {
      if (!statistic.reverse) {
        if (statistic.value < statistic.bad) {
          result = 'danger';
        } else if (statistic.value >= statistic.bad && statistic.value < statistic.perfect) {
          result = 'warning';
        } else if (statistic.value >= statistic.perfect) {
          result = 'success';
        }
      } else {
        /* reversed */
        if (statistic.value >= statistic.bad) {
          result = 'danger';
        } else if (statistic.value <= statistic.bad && statistic.value > statistic.perfect) {
          result = 'warning';
        } else if (statistic.value <= statistic.perfect) {
          result = 'success';
        }
      }

      if (statistic.value === this.invalidSqiValue) {
        result = 'invalid';
      }
    }
    return result;
  }

  getAverageSQIs(trips: Trip[], sqiConfigurations: SqiConfiguration[]): Statistic[] {
    let averageStatistics: Statistic[] = sqiConfigurations && sqiConfigurations.map((sqiConfiguration: SqiConfiguration) => {
      return {
        value: 0,
        title: sqiConfiguration.label,
        bad: sqiConfiguration.badValue,
        good: sqiConfiguration.goodValue,
        perfect: sqiConfiguration.targetValue,
        reverse: sqiConfiguration.reverse,
        sqiId: sqiConfiguration.id,
        shortDescription: sqiConfiguration.shortDescription,
        fullDescription: sqiConfiguration.description,
        mdDocumentation: sqiConfiguration.mdDocumentation,
        unit: sqiConfiguration.unit,
        cssClass: null
      }
    });

    const tripStatistics: Statistic[] = trips.reduce((acc: Statistic[], val) => acc.concat(val.statistics), []);

    tripStatistics.forEach(tripStatistic => {

      const averageStatistic = averageStatistics.find(averageStat => {
        return averageStat.sqiId === tripStatistic.sqiId
      });

      if (averageStatistic) {
        averageStatistic.value = averageStatistic.value + tripStatistic.value;
      }
    });

    averageStatistics.forEach((averageStatistic) => {
      averageStatistic.value = averageStatistic.value / trips.length;
      averageStatistic.cssClass = this.createSQICSSClass(averageStatistic);
    });

    return averageStatistics;
  }

  exportTrips(trips: Trip[], from?: Date, to?: Date): void {
    if (trips && trips.length > 0) {
      const flatTrips = this.flattenTrips(trips);
      const columns = this.createExportColumns(trips[0].statistics);
      const exportName = ExportFilenameService.generate('Trips-Overview', undefined, undefined, undefined, from, to);

      this.dataImportExportService.exportCSV(flatTrips, columns, exportName, ";");
    }
  }

  flattenTrips(trips: Trip[]): any[] {
    let flatTrips:any[] = [];

    trips.forEach(trip => {
      let flatTrip = {
        tripId: trip.id,
        service: trip.serviceId,
        train: trip.trainName,
        id: trip.trainId,
        route: trip.trajectory,
        start: this.datePipe.transform(trip.departure, "yyyy/MM/dd HH:mm") ?? "",
        stop: this.datePipe.transform(trip.arrival, "yyyy/MM/dd HH:mm") ?? "",
        duration: this.tripDurationPipe.transform(trip.arrival - trip.departure)
      }
      trip.statistics.forEach(statistic => {
        flatTrip[statistic.sqiId] = statistic.value ? statistic.value : "0";
      })
      flatTrips.push(flatTrip);
    });
    return flatTrips;
  }

  createExportColumns(statistics: Statistic[]): [string, string][] {
    let columns: [string, string][] = [["tripId", this.translate.instant('I18N_TRIP.ID')],
      ["service", this.translate.instant('I18N_TRIP.SERVICE')],
      ["train", this.translate.instant('I18N_TRIP.TRAIN')],
      ["id", this.translate.instant('I18N_TRIP.TRAIN_ID')],
      ["route", this.translate.instant('I18N_TRIP.ROUTE')],
      ["start", this.translate.instant('I18N_TRIP.START')],
      ["stop", this.translate.instant('I18N_TRIP.STOP')],
      ["duration", this.translate.instant('I18N_TRIP.DURATION')]];

    statistics.forEach(statistic => {
      columns.push([statistic.sqiId.toString(), this.translate.instant('I18N_TRIP.KPI_' + statistic.sqiId + "_LABEL")])
    });

    return columns;
  }
}
